import { useCallback, useState, useEffect } from "react";
import { notifications } from "@mantine/notifications";
import type { CassetteLabel } from "../components/accessioning/CassetteLabelPreview";
import {
  cassetteLabelPrinterService,
  type CassettePrinterStatus,
} from "../services/cassetteLabelPrinter.service";

/**
 * Hook that handles cassette label printing.
 *
 * - If an output folder is already selected, prints directly (no modal).
 * - If no folder is selected, prompts the user to pick one, then prints.
 * - Returns a `printLabels` function and folder status for UI rendering.
 */
export function useCassettePrint() {
  const [printerStatus, setPrinterStatus] = useState<CassettePrinterStatus>(
    cassetteLabelPrinterService.status
  );

  useEffect(() => {
    const unsubscribe = cassetteLabelPrinterService.onStatusChange((status) => {
      setPrinterStatus(status);
    });
    setPrinterStatus(cassetteLabelPrinterService.status);
    return unsubscribe;
  }, []);

  const printLabels = useCallback(
    async (labels: CassetteLabel[]): Promise<boolean> => {
      if (labels.length === 0) return false;

      if (!cassetteLabelPrinterService.isFolderSelected) {
        const selected =
          await cassetteLabelPrinterService.selectOutputDirectory();
        if (!selected) return false;
      }

      const result = await cassetteLabelPrinterService.printCassetteLabels(
        labels
      );

      if (result.success) {
        notifications.show({
          title: "Labels Saved",
          message: `${result.count} cassette label file(s) written to "${cassetteLabelPrinterService.folderName}".`,
          color: "green",
        });
        return true;
      }

      notifications.show({
        title: "Print Failed",
        message: result.error || "Could not write label files.",
        color: "red",
      });
      return false;
    },
    []
  );

  const changeFolder = useCallback(async () => {
    await cassetteLabelPrinterService.selectOutputDirectory();
  }, []);

  return {
    printLabels,
    changeFolder,
    printerStatus,
    folderName: cassetteLabelPrinterService.folderName,
    isFolderSelected: printerStatus === "folder-selected",
  };
}
